import React, { useCallback, useEffect, useRef, useState } from "react";
import "./VisorImagen.css";

type Punto = { x: number; y: number };
type Vista = Punto & { zoom: number };
const INICIAL: Vista = { x: 0, y: 0, zoom: 1 };
const limitar = (n: number) => Math.min(5, Math.max(1, n));
const distancia = (a: Punto, b: Punto) => Math.hypot(a.x - b.x, a.y - b.y);
const centro = (a: Punto, b: Punto): Punto => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/** Superficie reutilizable: zoom anclado al puntero y desplazamiento con mouse o dedos. */
export default function VisorImagen({ src, alt, onError, onLoad, onZoomChange, testId, className, onContextMenu, altura, onSwipe }: {
  src: string; alt: string; onError?: () => void; onLoad?: () => void;
  onZoomChange?: (zoom: number) => void; testId?: string; className?: string;
  onContextMenu?: React.MouseEventHandler<HTMLImageElement>; altura?: string; onSwipe?: (direccion: number) => void;
}) {
  const [vista, setVista] = useState<Vista>(INICIAL);
  const [arrastrando, setArrastrando] = useState(false);
  const [fallo, setFallo] = useState(false);
  const escenario = useRef<HTMLDivElement>(null);
  const puntos = useRef(new Map<number, Punto>());
  const gesto = useRef<{ inicio: Punto; multiple: boolean } | null>(null);

  useEffect(() => { setVista(INICIAL); puntos.current.clear(); gesto.current = null; setArrastrando(false); setFallo(false); }, [src]);
  useEffect(() => { onZoomChange?.(vista.zoom); }, [vista.zoom, onZoomChange]);

  const acercar = useCallback((factor: number, posicion?: Punto, desplazamiento: Punto = { x: 0, y: 0 }) => {
    const rect = escenario.current?.getBoundingClientRect();
    setVista((actual) => {
      const zoom = limitar(actual.zoom * factor);
      const ratio = zoom / actual.zoom;
      const ancla = rect && posicion ? { x: posicion.x - rect.left - rect.width / 2, y: posicion.y - rect.top - rect.height / 2 } : { x: 0, y: 0 };
      if (zoom === 1) return INICIAL;
      return { zoom, x: ancla.x - (ancla.x - actual.x) * ratio + desplazamiento.x, y: ancla.y - (ancla.y - actual.y) * ratio + desplazamiento.y };
    });
  }, []);

  useEffect(() => {
    const nodo = escenario.current;
    if (!nodo) return;
    const rueda = (e: WheelEvent) => {
      e.preventDefault();
      acercar(Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.045 : 0.0015)), { x: e.clientX, y: e.clientY });
    };
    nodo.addEventListener("wheel", rueda, { passive: false });
    return () => nodo.removeEventListener("wheel", rueda);
  }, [acercar, fallo]);

  const iniciar = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (e.pointerType === "touch" && puntos.current.size === 0) gesto.current = { inicio: { x: e.clientX, y: e.clientY }, multiple: false };
    else if (gesto.current) gesto.current.multiple = true;
    puntos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setArrastrando(true);
  };
  const mover = (e: React.PointerEvent<HTMLDivElement>) => {
    const anterior = puntos.current.get(e.pointerId);
    if (!anterior) return;
    const antes = Array.from(puntos.current.values());
    puntos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const despues = Array.from(puntos.current.values());
    if (antes.length >= 2 && despues.length >= 2) {
      const previo = centro(antes[0], antes[1]);
      const nuevo = centro(despues[0], despues[1]);
      const d = distancia(antes[0], antes[1]);
      if (d > 0) acercar(distancia(despues[0], despues[1]) / d, previo, { x: nuevo.x - previo.x, y: nuevo.y - previo.y });
    } else {
      const dx = e.clientX - anterior.x, dy = e.clientY - anterior.y;
      setVista((v) => v.zoom > 1 ? { ...v, x: v.x + dx, y: v.y + dy } : v);
    }
  };
  const terminar = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!puntos.current.has(e.pointerId)) return;
    puntos.current.delete(e.pointerId);
    const inicio = gesto.current;
    if (!puntos.current.size) {
      if (e.type === "pointerup" && inicio && !inicio.multiple && vista.zoom === 1) {
        const dx = e.clientX - inicio.inicio.x, dy = e.clientY - inicio.inicio.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) onSwipe?.(dx < 0 ? 1 : -1);
      }
      gesto.current = null;
    }
    setArrastrando(puntos.current.size > 0);
  };
  if (fallo) return <div className="visor-imagen-error" role="alert">No se pudo cargar la imagen. Vuelve a abrir el archivo para reintentar.</div>;
  return <div className="visor-imagen">
    <div ref={escenario} className={`visor-imagen-escenario${arrastrando ? " arrastrando" : ""}`} style={{ height: altura || "min(60dvh, 640px)" }}
      role="region" aria-label={`Visor de ${alt}. Usa la rueda para ampliar, arrastra para desplazar o usa Mayús y las flechas.`} tabIndex={0}
      onPointerDown={iniciar} onPointerMove={mover} onPointerUp={terminar} onPointerCancel={terminar} onLostPointerCapture={terminar}
      onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
      onDoubleClick={(e) => vista.zoom > 1 ? setVista(INICIAL) : acercar(2, { x: e.clientX, y: e.clientY })}
      onKeyDown={(e) => {
        if (e.shiftKey && vista.zoom > 1 && ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
          e.preventDefault(); e.stopPropagation();
          setVista((v) => ({ ...v, x: v.x + (e.key === "ArrowLeft" ? -40 : e.key === "ArrowRight" ? 40 : 0), y: v.y + (e.key === "ArrowUp" ? -40 : e.key === "ArrowDown" ? 40 : 0) }));
        } else if (e.key === "+" || e.key === "=") { e.preventDefault(); acercar(1.25); }
        else if (e.key === "-") { e.preventDefault(); acercar(1 / 1.25); }
        else if (e.key === "0") { e.preventDefault(); setVista(INICIAL); }
      }}>
      <img className={className} data-testid={testId} src={src} alt={alt} draggable={false} onError={() => { setFallo(true); onError?.(); }} onLoad={onLoad} onContextMenu={onContextMenu}
        style={{ transform: `translate(${vista.x}px, ${vista.y}px) scale(${vista.zoom})` }} />
    </div>
    <div className="visor-imagen-controles" aria-label="Controles de imagen">
      <button type="button" aria-label="Alejar imagen" onClick={() => acercar(1 / 1.25)} disabled={vista.zoom <= 1}><span aria-hidden="true">&#8722;</span></button>
      <span aria-live="polite">{Math.round(vista.zoom * 100)}%</span>
      <button type="button" aria-label="Acercar imagen" onClick={() => acercar(1.25)} disabled={vista.zoom >= 5}><span aria-hidden="true">+</span></button>
      <span className="visor-imagen-separador" />
      <button type="button" aria-label="Restablecer imagen" title="Ajustar imagen" onClick={() => setVista(INICIAL)}><span aria-hidden="true">&#8635;</span></button>
      <span className="visor-imagen-ayuda">Rueda para ampliar · arrastra para mover</span>
    </div>
  </div>;
}
